import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Alert, Box, Button, ButtonBase, Card, CardActionArea, Chip, Collapse, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { formatCurrency, formatPercentage } from "@/utils/format";
import {
  ABAS,
  agrupadorDoVerPor,
  DICA_ESTIMATIVA_PERDA,
  DIMENSAO_LABELS,
  DIMENSOES_COM_TOTAL,
  filtroDaLinha,
  type RupturaDimensao,
  type RupturaFiltros,
  type RupturaGeral,
  type RupturaResumo,
} from "@/api/ruptura.service";
import {
  CARDS_POR_VEZ,
  corPerc,
  formatNumero,
  percDe,
  RodapeCard,
  SEM_FILTRO,
  type AbrirProdutos,
  type NomeLoja,
} from "./rupturaComum";
import { useRupturaResumo } from "./useRupturaBusca";

// Cards da tela inicial da ruptura (geral e por dimensão) e a grade Ruptura/Excesso/Ok/Estimativa.
// O "Ver por" está no cabeçalho do card, em cada linha da grade e em cada item das listas:
// cada item abre a próxima dimensão ainda não usada no caminho, até esgotar as dimensões.

// Item do resumo que uma linha da grade representa: abre o "Ver por" com esse valor.
type MetricaResumo = "qtd_ruptura" | "qtd_excesso" | "qtd_ok" | "valor_risco";

export type LinhaGrade = {
  label: string;
  valor: string;
  perc?: number;
  cor?: string;
  dica?: string;
  metrica?: MetricaResumo;
  // Valor da métrica (sem formatar): base do % dos itens do "Ver por" da linha.
  quantidade?: number;
};

// Por onde o "Ver por" de uma linha abre, dentro do filtro do card.
type VerPorConfig = {
  // Dimensões já usadas no caminho (aba e itens abertos): não voltam como opção.
  usadas: RupturaDimensao[];
  // Dimensão em que as linhas abrem, quando não escolheram outra.
  dimensaoPadrao: RupturaDimensao;
  filtro: RupturaFiltros;
  clientId: string;
  nomeLoja: NomeLoja;
  onVerProdutos: AbrirProdutos;
};

// Situação dos produtos atrás de cada linha da grade (a estimativa só existe na ruptura).
const SITUACAO_DA_METRICA: Record<MetricaResumo, number> = { qtd_ruptura: 1, qtd_excesso: 3, qtd_ok: 5, valor_risco: 1 };

// Situação de cada contagem, no texto "x% em ruptura" do total do item.
const STATUS_DA_METRICA: Record<Exclude<MetricaResumo, "valor_risco">, string> = {
  qtd_ruptura: "em ruptura",
  qtd_excesso: "em excesso",
  qtd_ok: "ok",
};

const formatMetrica = (metrica: MetricaResumo, valor: number) =>
  metrica === "valor_risco" ? formatCurrency(valor) : formatNumero(valor, 0);

// Dimensões que ainda podem abrir num "Ver por", dado o que o caminho já usou. Responsável não
// tem chip: entra pelo "Problema" (agrupadorDoVerPor), que segue livre até os problemas abrirem.
const dimensoesLivres = (usadas: RupturaDimensao[]) => ABAS.filter((d) => !usadas.includes(d));

// Escolhida, se ainda livre; senão a primeira livre.
const dimensaoValida = (livres: RupturaDimensao[], escolhida: RupturaDimensao | undefined) =>
  escolhida && livres.includes(escolhida) ? escolhida : livres[0];

// Chips do "Ver por" de um card, linha ou item. Não propaga o clique: quem os carrega
// também abre/fecha ao ser clicado.
function SeletorVerPor({
  livres,
  valor,
  onChange,
}: {
  livres: RupturaDimensao[];
  valor: RupturaDimensao | undefined;
  onChange: (dimensao: RupturaDimensao) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} onClick={(e) => e.stopPropagation()}>
      {livres.map((d) => (
        <Chip
          key={d}
          size="small"
          label={DIMENSAO_LABELS[d]}
          color={d === valor ? "primary" : "default"}
          variant={d === valor ? "filled" : "outlined"}
          onClick={() => onChange(d)}
          sx={{ height: 20, fontSize: 11 }}
        />
      ))}
    </Stack>
  );
}

// Linhas Ruptura/Excesso/Ok/Estimativa de um card, cada contagem com o % do total.
const linhasDoResumo = (r: Record<MetricaResumo, number> & { qtd_total: number }): LinhaGrade[] => [
  { label: "Ruptura", valor: formatNumero(r.qtd_ruptura, 0), perc: percDe(r.qtd_ruptura, r.qtd_total), cor: r.qtd_ruptura > 0 ? "error.main" : undefined, metrica: "qtd_ruptura", quantidade: r.qtd_ruptura },
  { label: "Excesso", valor: formatNumero(r.qtd_excesso, 0), perc: percDe(r.qtd_excesso, r.qtd_total), metrica: "qtd_excesso", quantidade: r.qtd_excesso },
  { label: "Ok", valor: formatNumero(r.qtd_ok, 0), perc: percDe(r.qtd_ok, r.qtd_total), metrica: "qtd_ok", quantidade: r.qtd_ok },
  {
    label: "Estimativa de Perda",
    valor: formatCurrency(r.valor_risco),
    cor: "error.main",
    dica: DICA_ESTIMATIVA_PERDA,
    metrica: "valor_risco",
    quantidade: r.valor_risco,
  },
];

// Linha da grade ocupa as duas colunas (rótulo e valor) e herda as larguras delas.
const linhaGradeSx = { gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "subgrid", alignItems: "center" } as const;

export function GradeInfo({
  linhas,
  destaque = false,
  verPor,
  mt = 2,
}: {
  linhas: LinhaGrade[];
  destaque?: boolean;
  verPor?: VerPorConfig;
  mt?: number;
}) {
  // Linhas abertas: abrir uma não fecha as outras.
  const [abertas, setAbertas] = useState<Partial<Record<MetricaResumo, boolean>>>({});
  const alternar = (metrica: MetricaResumo) => setAbertas((a) => ({ ...a, [metrica]: !a[metrica] }));
  // "Ver por" escolhido em cada linha; sem escolha, vale o do card.
  const [escolhidas, setEscolhidas] = useState<Partial<Record<MetricaResumo, RupturaDimensao>>>({});
  const livres = verPor ? dimensoesLivres(verPor.usadas) : [];

  return (
    <Box
      sx={{
        mt,
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        columnGap: 3,
        rowGap: 0.75,
        bgcolor: destaque ? "background.paper" : "background.subtle",
        borderRadius: 2,
        p: 1.5,
      }}
    >
      {linhas.map((l) => {
        const metrica = verPor ? l.metrica : undefined;
        const estaAberta = !!metrica && !!abertas[metrica];
        const dimensao = dimensaoValida(livres, metrica && (escolhidas[metrica] ?? verPor?.dimensaoPadrao));
        const conteudo = (
          <>
            <Stack direction="row" alignItems="center" gap={1}>
              {metrica &&
                (estaAberta ? (
                  <KeyboardArrowDown sx={{ fontSize: 16, color: "text.secondary" }} />
                ) : (
                  <KeyboardArrowRight sx={{ fontSize: 16, color: "text.secondary" }} />
                ))}
              {l.dica ? (
                <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{l.dica}</Box>} arrow>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap", cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                  >
                    {l.label}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {l.label}
                </Typography>
              )}
              {metrica && dimensao && (
                <SeletorVerPor livres={livres} valor={dimensao} onChange={(d) => setEscolhidas((e) => ({ ...e, [metrica]: d }))} />
              )}
            </Stack>
            <Typography variant="caption" fontWeight={600} textAlign="right" sx={{ color: l.cor ?? "text.primary" }}>
              {l.valor}
              {l.perc !== undefined && (
                <Typography component="span" variant="caption" sx={{ ml: 1, display: "inline-block", minWidth: 52, color: l.cor ?? "text.secondary" }}>
                  {formatPercentage(l.perc)}
                </Typography>
              )}
            </Typography>
          </>
        );
        return (
          <Fragment key={l.label}>
            {metrica ? (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => alternar(metrica)}
                onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && alternar(metrica)}
                sx={{ ...linhaGradeSx, cursor: "pointer", borderRadius: 1 }}
              >
                {conteudo}
              </Box>
            ) : (
              <Box sx={linhaGradeSx}>{conteudo}</Box>
            )}
            {estaAberta && metrica && verPor && dimensao && (
              <Box sx={{ gridColumn: "1 / -1", pl: 2, pb: 1 }}>
                <VerPorMetrica metrica={metrica} {...verPor} dimensao={dimensao} destaque={destaque} totalPai={l.quantidade ?? 0} />
                <Button size="small" onClick={() => verPor.onVerProdutos(null, { ...verPor.filtro, cod_problema: SITUACAO_DA_METRICA[metrica] })}>
                  Ver produtos →
                </Button>
              </Box>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

// "Ver por" de uma linha da grade ou de um item: lista o valor daquela métrica em cada linha
// do resumo da dimensão escolhida, maior primeiro. Cada item abre a dimensão seguinte.
// O % de cada item é sobre o valor da linha/item de cima (totalPai), não sobre o total do item.
function VerPorMetrica({
  metrica,
  dimensao,
  usadas,
  filtro,
  clientId,
  nomeLoja,
  onVerProdutos,
  destaque,
  totalPai,
}: Omit<VerPorConfig, "dimensaoPadrao"> & { dimensao: RupturaDimensao; metrica: MetricaResumo; destaque: boolean; totalPai: number }) {
  // "Problema" escolhido antes de um responsável lista os responsáveis.
  const agrupador = agrupadorDoVerPor(dimensao, usadas);
  const { dados, erro, loading } = useRupturaResumo(agrupador, filtro, clientId);
  const linhas = useMemo(
    () => (dados ?? []).filter((n) => n[metrica] > 0).sort((a, b) => b[metrica] - a[metrica]),
    [dados, metrica],
  );
  // De 20 em 20 no "Ver mais" (Fornecedor tem milhares de linhas). Trocar o "Ver por"
  // volta ao primeiro lote.
  const [lote, setLote] = useState({ dimensao, qtd: CARDS_POR_VEZ });
  const qtd = lote.dimensao === dimensao ? lote.qtd : CARDS_POR_VEZ;
  const restantes = linhas.length - qtd;

  return (
    <Box>
      {loading ? (
        <LinearProgress sx={{ my: 1 }} />
      ) : erro ? (
        <Alert severity="error" sx={{ my: 1 }}>
          {erro}
        </Alert>
      ) : linhas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
          Nada para mostrar.
        </Typography>
      ) : (
        <Stack gap={0.5} sx={{ my: 1 }}>
          {linhas.slice(0, qtd).map((n) => (
            <ItemVerPor
              key={n.chave}
              item={n}
              metrica={metrica}
              usadas={[...usadas, agrupador]}
              filtro={filtro}
              clientId={clientId}
              nomeLoja={nomeLoja}
              onVerProdutos={onVerProdutos}
              destaque={destaque}
              totalPai={totalPai}
            />
          ))}
          {restantes > 0 && (
            <Button size="small" sx={{ alignSelf: "center" }} onClick={() => setLote({ dimensao, qtd: qtd + CARDS_POR_VEZ })}>
              Carregar mais
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}

// Item de um "Ver por": clicar abre a próxima dimensão livre dentro dele (com os chips para
// trocá-la) e o atalho para os produtos. Sem dimensão livre, clicar abre os produtos.
function ItemVerPor({
  item,
  metrica,
  usadas,
  filtro,
  clientId,
  nomeLoja,
  onVerProdutos,
  destaque,
  totalPai,
}: Omit<VerPorConfig, "dimensaoPadrao"> & { item: RupturaResumo; metrica: MetricaResumo; destaque: boolean; totalPai: number }) {
  const [aberto, setAberto] = useState(false);
  const [escolhida, setEscolhida] = useState<RupturaDimensao>();
  const livres = dimensoesLivres(usadas);
  const dimensao = dimensaoValida(livres, escolhida);
  // O item vira filtro do que abre dentro dele.
  const filtroItem = useMemo(() => ({ ...filtro, ...filtroDaLinha(item) }), [filtro, item]);
  const verProdutos = () => onVerProdutos(item, { ...filtro, cod_problema: SITUACAO_DA_METRICA[metrica] });
  const vermelho = metrica === "valor_risco" || (metrica === "qtd_ruptura" && item[metrica] > 0);
  // Total de itens do comprador/categoria/fornecedor e quanto dele está na situação da linha.
  const status = metrica !== "valor_risco" && DIMENSOES_COM_TOTAL.includes(item.dimensao) ? STATUS_DA_METRICA[metrica] : null;

  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => (dimensao ? setAberto((v) => !v) : verProdutos())}
        onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && (dimensao ? setAberto((v) => !v) : verProdutos())}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          gap: 1.5,
          px: 2,
          py: 0.75,
          bgcolor: destaque ? "background.subtle" : "background.paper",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {aberto ? (
          <KeyboardArrowDown sx={{ fontSize: 16, color: "text.secondary" }} />
        ) : (
          <KeyboardArrowRight sx={{ fontSize: 16, color: "text.secondary" }} />
        )}
        <Typography variant="body2" sx={{ minWidth: 0, flex: "0 1 auto" }} noWrap>
          {item.dimensao === "loja" ? nomeLoja(item.chave) : item.rotulo}
        </Typography>
        {dimensao && <SeletorVerPor livres={livres} valor={dimensao} onChange={setEscolhida} />}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ whiteSpace: "nowrap", color: vermelho ? "error.main" : "text.primary" }}
        >
          {formatMetrica(metrica, item[metrica])}
        </Typography>
        {status && (
          <Typography variant="caption" color="text.secondary" sx={{ width: 220, flexShrink: 0, whiteSpace: "nowrap" }}>
            de {formatNumero(item.qtd_total, 0)} itens · {formatPercentage(percDe(item[metrica], item.qtd_total))} {status}
          </Typography>
        )}
        {metrica !== "valor_risco" && (
            <Typography variant="caption" sx={{ minWidth: 52, textAlign: "right", color: vermelho ? "error.main" : "text.secondary" }}>
              {formatPercentage(percDe(item[metrica], totalPai))}
            </Typography>
        )}
      </Box>
      {aberto && dimensao && (
        <Box sx={{ pl: 2 }}>
          <VerPorMetrica
            metrica={metrica}
            dimensao={dimensao}
            usadas={usadas}
            filtro={filtroItem}
            clientId={clientId}
            nomeLoja={nomeLoja}
            onVerProdutos={onVerProdutos}
            destaque={!destaque}
            totalPai={item[metrica]}
          />
          <Button size="small" onClick={verProdutos}>
            Ver produtos →
          </Button>
        </Box>
      )}
    </Box>
  );
}

// Cabeçalho dos cards geral e por dimensão: nome e itens em ruptura / total.
function CabecalhoCard({
  nome,
  expanded,
  qtdRuptura,
  qtdTotal,
  perc,
  seletor,
}: {
  nome: string;
  expanded: boolean;
  qtdRuptura: number;
  qtdTotal: number;
  perc: number;
  seletor?: ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
      <Typography variant="subtitle2" sx={{ minWidth: 0, textTransform: "uppercase", color: "text.secondary" }} noWrap>
        {nome}
      </Typography>
      {seletor}
      <Box sx={{ flex: 1 }} />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          Itens em ruptura / total de itens
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>
            <Box component="span" sx={{ color: qtdRuptura > 0 ? "error.main" : undefined }}>
              {formatNumero(qtdRuptura, 0)}
            </Box>{" "}
            / {formatNumero(qtdTotal, 0)}
          </Typography>
          <Chip size="small" color={qtdRuptura > 0 ? "error" : corPerc(perc)} label={formatPercentage(perc)} />
        </Stack>
      </Box>
    </Stack>
  );
}

// Resultado geral da empresa (query ruptura), no topo como o card da companhia no Flash.
// valor_risco não vem nessa query: é a soma das linhas do agrupador atual, que
// particionam o mesmo universo.
export function GeralCard({
  nome,
  geral,
  valorRisco,
  aba,
  clientId,
  nomeLoja,
  onVerProdutos,
  onVerTodos,
}: {
  nome: string;
  geral: RupturaGeral;
  valorRisco: number;
  aba: RupturaDimensao;
  clientId: string;
  nomeLoja: NomeLoja;
  onVerProdutos: AbrirProdutos;
  // Lista de todos os produtos, sem filtro nenhum.
  onVerTodos: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const livres = dimensoesLivres([aba]);
  const [escolhida, setEscolhida] = useState<RupturaDimensao>();
  const verPor = dimensaoValida(livres, escolhida);

  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => setExpanded((v) => !v)} sx={{ p: 2 }}>
        <CabecalhoCard
          nome={nome}
          expanded={expanded}
          qtdRuptura={geral.qtd_ruptura}
          qtdTotal={geral.qtd_total}
          perc={percDe(geral.qtd_ruptura, geral.qtd_total)}
          seletor={<SeletorVerPor livres={livres} valor={verPor} onChange={setEscolhida} />}
        />
      </CardActionArea>
      {/* Fora do CardActionArea: as linhas da grade são clicáveis (abrem o "Ver por"). */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <GradeInfo
            mt={0}
            verPor={{ usadas: [aba], dimensaoPadrao: verPor!, filtro: SEM_FILTRO, clientId, nomeLoja, onVerProdutos }}
            linhas={linhasDoResumo({ ...geral, valor_risco: valorRisco })}
          />
        </Box>
      </Collapse>

      {expanded && <RodapeCard label="Ver todos os produtos →" onClick={onVerTodos} />}
    </Card>
  );
}

export function ResumoCard({
  item,
  nome,
  aba,
  clientId,
  nomeLoja,
  onVerProdutos,
}: {
  item: RupturaResumo;
  nome: string;
  aba: RupturaDimensao;
  clientId: string;
  nomeLoja: NomeLoja;
  onVerProdutos: AbrirProdutos;
}) {
  const [expanded, setExpanded] = useState(false);
  const livres = dimensoesLivres([aba]);
  const [escolhida, setEscolhida] = useState<RupturaDimensao>();
  const verPor = dimensaoValida(livres, escolhida);
  // "Ver por" nas linhas da grade: a linha do card vira filtro de ruptura_resumo, então
  // a dimensão do seletor abre dentro dela.
  const filtro = useMemo(() => filtroDaLinha(item), [item]);

  return (
    <Card variant="outlined" sx={{ ml: 3, bgcolor: "background.subtle", borderLeftWidth: 3, borderLeftColor: "primary.light" }}>
      <CardActionArea onClick={() => setExpanded((v) => !v)} sx={{ p: 2 }}>
        <CabecalhoCard
          nome={nome}
          expanded={expanded}
          qtdRuptura={item.qtd_ruptura}
          qtdTotal={item.qtd_total}
          perc={item.perc_ruptura}
          seletor={<SeletorVerPor livres={livres} valor={verPor} onChange={setEscolhida} />}
        />
      </CardActionArea>
      {/* Fora do CardActionArea: as linhas da grade são clicáveis (abrem o "Ver por"). */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <GradeInfo
            destaque
            mt={0}
            verPor={{ usadas: [aba], dimensaoPadrao: verPor!, filtro, clientId, nomeLoja, onVerProdutos }}
            linhas={linhasDoResumo(item)}
          />
        </Box>
      </Collapse>

      {expanded && item.qtd_total > 0 && (
        <RodapeCard label={`Ver produtos de ${nome.toUpperCase()} →`} onClick={() => onVerProdutos(item, SEM_FILTRO)} />
      )}
    </Card>
  );
}
